import { parseMarkdown } from './markdown'
import {
  TemplateFormatError,
  type FieldType,
  type FormField,
  type FormTemplate,
  type Role,
  type SignatureSpec,
} from './types'

const FENCE = '---'

/**
 * Reads a template file: a JSON frontmatter block fenced by `---` lines, then
 * the markdown body. A port of `TemplateParser.kt`.
 *
 * Nothing is accepted half-parsed. A template that names a field its body does
 * not declare, or vice versa, is rejected at import rather than going quietly
 * missing on set.
 */
export function parseTemplate(source: string): FormTemplate {
  // Templates edited on Windows or exported from a phone often arrive with a
  // BOM and CRLF endings; neither should be a parse error.
  const text = source.replace(/^﻿/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const lines = text.split('\n')

  if (lines[0]?.trim() !== FENCE) {
    throw new TemplateFormatError('Template must start with a --- frontmatter fence.')
  }
  const close = lines.slice(1).findIndex((l) => l.trim() === FENCE)
  if (close < 0) {
    throw new TemplateFormatError('Frontmatter is never closed by a second --- line.')
  }

  const json = lines.slice(1, close + 1).join('\n')
  const body = lines.slice(close + 2).join('\n')

  let meta: Record<string, unknown>
  try {
    meta = JSON.parse(json)
  } catch (e) {
    throw new TemplateFormatError(`Frontmatter is not valid JSON: ${(e as Error).message}`)
  }

  const fields = parseFields(meta.fields)
  if (!fields.length) throw new TemplateFormatError('Template declares no fields.')
  const signatures = parseSignatures(meta.signatures)
  if (!signatures.length) throw new TemplateFormatError('Template declares no signatures.')

  const id = str(meta.id)
  if (!id) throw new TemplateFormatError('Template is missing "id".')
  const nameFieldKey = str(meta.nameField)
  if (!nameFieldKey) throw new TemplateFormatError('Template is missing "nameField".')
  if (!fields.some((f) => f.key === nameFieldKey)) {
    throw new TemplateFormatError(`nameField "${nameFieldKey}" does not match any declared field.`)
  }
  const emailFieldKey = str(meta.emailField) || null
  if (emailFieldKey && !fields.some((f) => f.key === emailFieldKey)) {
    throw new TemplateFormatError(`emailField "${emailFieldKey}" does not match any declared field.`)
  }

  const blocks = parseMarkdown(body)
  const declared = new Set(fields.map((f) => f.key))
  const signatureKeys = new Set(signatures.map((s) => s.key))
  for (const block of blocks) {
    if (block.kind === 'fieldLine' && !declared.has(block.key)) {
      throw new TemplateFormatError(
        `Body references {{field:${block.key}}} but no such field is declared.`,
      )
    }
    if (block.kind === 'signature' && !signatureKeys.has(block.key)) {
      throw new TemplateFormatError(
        `Body references {{sig:${block.key}}} but no such signature is declared.`,
      )
    }
  }

  return {
    id,
    title: str(meta.title) || id,
    version: typeof meta.version === 'number' ? meta.version : 1,
    description: str(meta.description),
    nameFieldKey,
    emailFieldKey,
    fields,
    signatures,
    agreeText: str(meta.agreeText) || 'I have read this document in full and I agree to it.',
    body: blocks,
  }
}

function parseFields(raw: unknown): FormField[] {
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  return raw.map((entry, i) => {
    const o = entry as Record<string, unknown>
    const key = str(o.key)
    if (!key) throw new TemplateFormatError(`Field at index ${i} has no "key".`)
    if (seen.has(key)) throw new TemplateFormatError(`Duplicate field key "${key}".`)
    seen.add(key)

    const type = fieldType(str(o.type))
    const options = Array.isArray(o.options) ? o.options.filter((v): v is string => typeof v === 'string') : []
    if (type === 'choice' && options.length < 2) {
      throw new TemplateFormatError(`Choice field "${key}" needs at least two options.`)
    }
    const fallback = str(o.default)
    if (type === 'choice' && fallback && !options.includes(fallback)) {
      throw new TemplateFormatError(`Default for choice field "${key}" is not one of its options.`)
    }

    return {
      key,
      label: str(o.label) || key,
      type,
      role: str(o.role).toLowerCase() === 'producer' ? ('producer' as Role) : ('participant' as Role),
      required: o.required === true,
      remember: o.remember === true,
      hint: str(o.hint),
      default: fallback,
      options,
      allowOther: o.allowOther === true,
      otherLabel: str(o.otherLabel) || 'Something else',
    }
  })
}

function parseSignatures(raw: unknown): SignatureSpec[] {
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  return raw.map((entry, i) => {
    const o = entry as Record<string, unknown>
    const key = str(o.key)
    if (!key) throw new TemplateFormatError(`Signature at index ${i} has no "key".`)
    if (seen.has(key)) throw new TemplateFormatError(`Duplicate signature key "${key}".`)
    seen.add(key)
    return {
      key,
      label: str(o.label) || 'Signature',
      // Matches the Kotlin default: signatures are required unless said otherwise.
      required: o.required !== false,
    }
  })
}

function fieldType(raw: string): FieldType {
  switch (raw.toLowerCase()) {
    case 'multiline':
      return 'multiline'
    case 'email':
      return 'email'
    case 'phone':
      return 'phone'
    case 'date':
      return 'date'
    case 'choice':
      return 'choice'
    default:
      return 'text'
  }
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : ''
}
